import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Label } from "../../ui/label";
import { Loader2Icon } from "lucide-react";
import { TestSetService } from "../../../services/TestSetService";
import { TestSet } from "../../../model_gen/evaluation";
import { DatasetService } from "../../../services/DatasetService";
import { FinetuneDataset } from "../../../model_gen/finetuning";

interface AddSemanticLayerModalProps {
  isVisible: boolean;
  onClose: () => void;
  onAddLayer: (layerData: {
    entityType: string;
    entityTypeId: number;
  }) => void;
  datasetId?: number;
  testsetId?: number;
  apiKey: string;
}

const AddSemanticLayerModal: React.FC<AddSemanticLayerModalProps> = ({
  isVisible,
  onClose,
  onAddLayer,
  datasetId,
  testsetId,
  apiKey
}) => {
  const [layerType, setLayerType] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [layerOptions, setLayerOptions] = useState<{ value: string; label: string }[]>([]);
  const [entityOptions, setEntityOptions] = useState<TestSet[] | FinetuneDataset[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<number | undefined>(undefined);
  const [loadingEntity, setLoadingEntity] = useState<boolean>(false);

  // Set layer options based on datasetId or testsetId
  useEffect(() => {
    if (datasetId) {
      setLayerOptions([{ value: "test_set", label: "Test Set" }]);
    } else if (testsetId) {
      setLayerOptions([{ value: "dataset", label: "Dataset" }]);
    } else {
      setLayerOptions([
        { value: "test_set", label: "Test Set" },
        { value: "dataset", label: "Dataset" }
      ]);
    }
  }, [datasetId, testsetId]);

  // Fetch test sets when layer type is "test_set" and datasetId is available
  useEffect(() => {
    if (layerType === "test_set" && datasetId) {
      setLoadingEntity(true);
      setSelectedEntityId(undefined);
      
      const subscription = TestSetService.getTestSetsByDatasetId(datasetId, setLoadingEntity, apiKey)
        .subscribe({
          next: (response: any) => {
            if (response) {
              setEntityOptions(response);
            } else {
              setEntityOptions([]);
            }
            setLoadingEntity(false);
          },
          error: (error) => {
            console.error("Error fetching test sets:", error);
            setEntityOptions([]);
            setLoadingEntity(false);
          }
        });
      
      return () => subscription.unsubscribe();
    }
  }, [layerType, datasetId, apiKey]);

  useEffect(() => {
    if (layerType === "dataset" && testsetId) {
      setLoadingEntity(true);
      setSelectedEntityId(undefined);
      
      const subscription = DatasetService.getDatasetsByTestSet(testsetId, setLoadingEntity, apiKey)
        .subscribe({
          next: (response: any) => {
            if (response) {
              setEntityOptions(response);
            } else {
              setEntityOptions([]);
            }
            setLoadingEntity(false);
          },
          error: (error) => {
            console.error("Error fetching datasets:", error);
            setEntityOptions([]);
            setLoadingEntity(false);
          }
        });
      
      return () => subscription.unsubscribe();
    }
  }, [layerType, testsetId, apiKey]);

  const getEntityDisplayName = (entity: TestSet | FinetuneDataset) => {
    if (layerType === "test_set") {
      return entity.name;
    } else if (layerType === "dataset") {
      return entity.name;
    }
  };

  const handleSubmit = () => {
    if (!layerType) {
      return;
    }

    if (!selectedEntityId) {
      return;
    }

    if (isSubmitting) {
      return; // Prevent multiple submissions
    }

    setIsSubmitting(true);
    
    // Call the onAddLayer callback with the selected entity type and ID
    onAddLayer({
      entityType: layerType,
      entityTypeId: selectedEntityId
    });
    
    // Reset form and close modal
    resetForm();
    onClose();
    setIsSubmitting(false);
  };

  const resetForm = () => {
    setLayerType("");
    setSelectedEntityId(undefined);
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isVisible} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-gc-med text-foreground">
            Add Semantic Layer
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-gc-med text-foreground">Layer Type</Label>
            <Select value={layerType} onValueChange={setLayerType}>
              <SelectTrigger>
                <SelectValue placeholder="Select layer type" />
              </SelectTrigger>
              <SelectContent>
                {layerOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {layerType === "test_set" && (
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">Test Set</Label>
              <Select 
                value={selectedEntityId?.toString()} 
                onValueChange={(value) => setSelectedEntityId(Number(value))}
                disabled={loadingEntity}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingEntity ? "Loading..." : "Select test set"} />
                </SelectTrigger>
                <SelectContent>
                  {entityOptions.map((entity) => (
                    <SelectItem key={entity.id} value={entity.id.toString()}>
                      {getEntityDisplayName(entity)}
                    </SelectItem>
                  ))}
                  {entityOptions.length === 0 && !loadingEntity && (
                    <div className="px-2 py-1 text-sm text-muted-foreground">
                      No test sets found
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {layerType === "dataset" && (
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">Dataset</Label>
              <Select 
                value={selectedEntityId?.toString()} 
                onValueChange={(value) => setSelectedEntityId(Number(value))}
                disabled={loadingEntity}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingEntity ? "Loading..." : "Select dataset"} />
                </SelectTrigger>
                <SelectContent>
                  {entityOptions.map((entity) => (
                    <SelectItem key={entity.id} value={entity.id.toString()}>
                      {getEntityDisplayName(entity)}
                    </SelectItem>
                  ))}
                  {entityOptions.length === 0 && !loadingEntity && (
                    <div className="px-2 py-1 text-sm text-muted-foreground">
                      No datasets found
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={handleCancel} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!layerType || !selectedEntityId || isSubmitting || loadingEntity}
          >
            {isSubmitting && <Loader2Icon className="w-4 h-4 animate-spin mr-2" />}
            Add Layer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddSemanticLayerModal; 